import { cn } from '@/lib/utils'
import { Dices, ShoppingCart, Skull } from 'lucide-react'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import EncounterWishlistTooltip from './EncounterWishlistTooltip'
import WishLegacyPriority from 'app/frontend/types/custom/WishLegacyPriority'
import WishLegacyPriorityDot from 'app/frontend/base/components/WishLegacyPriorityDot'
import { EventActivityParticipantEntry } from './eventActivityParticipantEntry'
import {
  countSelectedActivities,
  resolveEventParticipantCharacter,
} from './eventActivityParticipants'
import { useTeamEventParticipantsContext } from '../context/TeamEventParticipantsContext'
import { usePageInfo } from 'app/frontend/base/config/queries'
import EventActivitySelectedCount from './EventActivitySelectedCount'

type Props = {
  entry: EventActivityParticipantEntry
  activity: wowaudit.EventActivity
  className?: string
  contentOnly?: boolean
  showSelectedCount?: boolean
  isNarrowColumn?: boolean
}

const EventActivityParticipantActivityPlanningInfo = ({
  entry,
  activity,
  className,
  contentOnly = false,
  showSelectedCount = false,
  isNarrowColumn = false,
}: Props) => {
  const { planningInfoByActivityAndCharacter, activeActivityIds } =
    useTeamEventParticipantsContext()
  const {
    data: { currentSeason },
  } = usePageInfo()
  const selectedCount = countSelectedActivities(entry, activeActivityIds)
  const displayCharacter = resolveEventParticipantCharacter(entry, activity.id)
  const isBonusRollTarget =
    !!currentSeason.bonus_rolls_available &&
    activity.encounter?.id != null &&
    displayCharacter.bonusRollEncounterIds.includes(activity.encounter.id)
  const planningInfo = planningInfoByActivityAndCharacter.get(
    `${activity.id}:${displayCharacter.id}`,
  )
  const planningKillCount = planningInfo?.killCount ?? 0
  const planningWishlistUpgrade = planningInfo?.wishlistUpgrade ?? 0
  const planningWishlistEdited = planningInfo?.edited ?? false
  const planningWishlistItems = planningInfo?.wishlistItems ?? []
  const allPlanningWishesManual =
    planningWishlistItems.length > 0 &&
    planningWishlistItems.every(
      (item) =>
        (item.score?.manual?.length ?? 0) > 0 ||
        item.score?.percentage === null,
    )
  const manualUpgradeLevels = Array.from(
    new Set(planningWishlistItems.flatMap((item) => item.score?.manual ?? [])),
  ) as WishLegacyPriority[]

  const hasWishlist =
    planningWishlistUpgrade > 0 || allPlanningWishesManual || contentOnly
  const hasRightGroup = hasWishlist || isBonusRollTarget
  const hideIcons = isNarrowColumn && hasWishlist

  const killsContent = (
    <span className='flex shrink-0 items-center gap-1'>
      {showSelectedCount ? (
        <span className='flex items-center gap-1 mr-1'>
          <span className='text-muted-foreground text-[10px] font-semibold'>
            in
          </span>
          <EventActivitySelectedCount
            selectedCount={selectedCount}
            className='font-mono'
          />
        </span>
      ) : null}

      {hideIcons ? null : (
        <Tooltip delayDuration={400}>
          <TooltipTrigger>
            <Skull size={13} className='opacity-70' />
          </TooltipTrigger>
          <TooltipContent>
            {planningKillCount} total kill{planningKillCount === 1 ? '' : 's'}
          </TooltipContent>
        </Tooltip>
      )}
      <span
        className={
          planningKillCount > 0
            ? 'text-success-foreground'
            : 'text-muted-foreground'
        }
      >
        {planningKillCount}
      </span>
    </span>
  )
  const wishlistContent = hasWishlist ? (
    <EncounterWishlistTooltip items={planningWishlistItems}>
      <span className='flex items-center gap-1'>
        {hideIcons ? null : <ShoppingCart size={13} className='opacity-70' />}
        <span
          className={cn(
            planningWishlistEdited
              ? 'text-warning-foreground'
              : planningWishlistUpgrade > 0
              ? 'text-success-foreground'
              : 'text-foreground',
          )}
        >
          {allPlanningWishesManual ? (
            <span className='flex items-center gap-1'>
              {manualUpgradeLevels.map((level) => (
                <WishLegacyPriorityDot key={level} priority={level} />
              ))}
            </span>
          ) : (
            <>
              {planningWishlistUpgrade.toLocaleString('en-US', {
                maximumFractionDigits: 1,
                minimumFractionDigits: 0,
              })}
              %
            </>
          )}
        </span>
      </span>
    </EncounterWishlistTooltip>
  ) : null

  const content = (
    <div className='flex flex-wrap items-center justify-between gap-x-2'>
      {killsContent}
      {hasRightGroup ? (
        <div className='ml-auto flex shrink-0 items-center gap-1.5'>
          {isBonusRollTarget ? (
            <Tooltip delayDuration={400}>
              <TooltipTrigger>
                <Dices size={13} className='text-info-foreground' />
              </TooltipTrigger>
              <TooltipContent>Bonus roll target</TooltipContent>
            </Tooltip>
          ) : null}
          {wishlistContent}
        </div>
      ) : null}
    </div>
  )

  if (contentOnly) return content

  return (
    <div
      className={cn(
        'rounded-b-md border border-border bg-card-nested px-1.5 pt-px pb-0.5 text-xs',
        className,
      )}
    >
      {content}
    </div>
  )
}

export default EventActivityParticipantActivityPlanningInfo
